/** Admin API `dealType` values and UI labels — single source of truth. */
export const DEAL_TYPE_CONFIG = {
  PHYSICAL_GOLD: {
    value: 'PHYSICAL_GOLD',
    label: 'Physical Gold Deal',
  },
  ELECTRONIC: {
    value: 'ELECTRONIC',
    label: 'Electronic Deal',
  },
} as const;

export type DealType =
  (typeof DEAL_TYPE_CONFIG)[keyof typeof DEAL_TYPE_CONFIG]['value'];

export type DealTypeKey = keyof typeof DEAL_TYPE_CONFIG;

export type DealFulfillment = 'drop_shipping' | 'inventory';
export type DealPricingMode = 'FIXED' | 'DYNAMIC';

export const DEAL_TYPE_OPTIONS = Object.values(DEAL_TYPE_CONFIG).map(
  ({ label, value }) => ({ label, value })
);

const LABEL_BY_VALUE = Object.fromEntries(
  Object.values(DEAL_TYPE_CONFIG).map(({ value, label }) => [value, label])
) as Record<DealType, string>;

/** Wizard + API defaults when deal type is Electronic. */
export const ELECTRONIC_DEAL_DEFAULTS = {
  pricingMode: 'FIXED',
  fulfillment: 'inventory',
  weight: '',
} as const satisfies {
  pricingMode: DealPricingMode;
  fulfillment: DealFulfillment;
  weight: string;
};

export function isDealType(
  dealType: string | undefined,
  key: DealTypeKey
): boolean {
  if (!dealType) {
    return false;
  }
  return dealType === DEAL_TYPE_CONFIG[key].value;
}

export function getDealTypeLabel(dealType: string): string {
  return LABEL_BY_VALUE[dealType as DealType] ?? dealType;
}

export function resolvePricingMode(
  dealType: string | undefined,
  pricingMode?: string
): DealPricingMode {
  if (isDealType(dealType, 'ELECTRONIC')) {
    return 'FIXED';
  }
  if (pricingMode === 'FIXED' || pricingMode === 'DYNAMIC') {
    return pricingMode;
  }
  return 'FIXED';
}

export function resolveFulfillment(
  dealType: string | undefined,
  fulfillment?: string
): DealFulfillment {
  if (isDealType(dealType, 'ELECTRONIC')) {
    return 'inventory';
  }
  if (fulfillment === 'inventory') {
    return 'inventory';
  }
  return 'drop_shipping';
}

export function formatWeightForForm(
  dealType: string | undefined,
  weight: unknown
): string {
  if (isDealType(dealType, 'ELECTRONIC')) {
    return '';
  }
  if (weight == null || weight === '') {
    return '';
  }
  return String(weight);
}
